import { logger } from '../../utils/logger.js'
import maplibregl, { addProtocol, removeProtocol } from 'maplibre-gl'
import { Capacitor } from '@capacitor/core'
import { fromUrl, type GeoTIFF, type GeoTIFFImage } from 'geotiff'
import NwmWorker from './nwm-worker.ts?worker'
import { timelineService, type TimelineState } from '../timeline/timeline-service.js'
import { NWM_CONFIGS, type NwmVariable, type NwmVariableConfig } from './nwm-types.js'
import {
  snapToAvailableTimestamp,
  generateTimestampCandidates,
  generateGoesTimestampCandidates,
  tileBoundsToLatLng,
  zoomToOverviewIndex,
  computeWindFraction,
  generateWindGeoJSON,
} from '../../lib/nwm-utils.js'
import { addOverlayLayer } from '../../components/map/layer-order-manager.js'

const log = logger.create('terrain')

function isAborted(signal: AbortSignal): boolean {
  return signal.aborted
}

const TILE_SIZE = 256
const WORKER_POOL_SIZE = 2
const DEFAULT_OPACITY = 0.6
const COG_CACHE_SIZES: Record<string, number> = {
  goes: 48,
  mrms: 48,
  hrrr: 8,
  land: 8,
  forcing: 8,
}
const MIN_ZOOM = 4
const MAX_ZOOM = 12
const COG_ORIGIN = Capacitor.isNativePlatform() ? 'https://taselith.com' : ''
const COG_BASE_URL = `${COG_ORIGIN}/data/nwm`

interface CogMetadata {
  bbox: number[]
  mainWidth: number
  mainHeight: number
  pxPerDegX: number
  pxPerDegY: number
}

interface CogCacheEntry {
  tiff: GeoTIFF
  images: Map<number, GeoTIFFImage>
  imagesPending: Map<number, Promise<GeoTIFFImage>>
  metadata?: CogMetadata
  metadataPending: Promise<CogMetadata> | undefined
}

/**
 * Generic NWM variable service. One instance per variable, each managing
 * its own MapLibre protocol, sources, layers, worker pool, and COG cache.
 */
class NwmVariableService {
  private config: NwmVariableConfig
  private protocol: string
  private buffers: readonly [
    { sourceId: string; layerId: string },
    { sourceId: string; layerId: string },
  ]

  private workers: Worker[] = []
  private workerIndex = 0
  private map: maplibregl.Map | null = null
  private _enabled = false
  private _opacity = DEFAULT_OPACITY
  private _smoothing = true
  private _enabledPrecipTypes = 0b1111
  private pendingRequests = new Map<string, AbortController>()
  private nextRequestId = 0
  private cogCache = new Map<string, CogCacheEntry>()
  private cogPending = new Map<string, Promise<CogCacheEntry | null>>()
  private _selectedTimestamp = ''
  private _availableTimestamps: string[] = []
  private dateVersion = 0

  // Double-buffer state (raster path only)
  private frontBuffer = 0
  private backBufferTimestamp = ''
  private backBufferLoading = false
  private onDeckTimestamp = ''
  private sourceDataHandler: ((e: maplibregl.MapSourceDataEvent) => void) | null = null
  private swapTimer: ReturnType<typeof setTimeout> | null = null

  // Wind GeoJSON state (vector path only)
  private windSourceId: string
  private windLayerId: string
  private windMoveHandler: (() => void) | null = null
  private windDebounceTimer: ReturnType<typeof setTimeout> | null = null
  private windLoading = false
  private windOnDeckTs = ''
  private windViewportDirty = false
  private windCache: {
    // Viewport grid params
    spacing: number
    west: number
    east: number
    south: number
    north: number
    // Pixel mapping from geographic coords to raster window
    bbox: number[]
    pxPerDegX: number
    pxPerDegY: number
    scaleX: number
    scaleY: number
    winLeft: number
    winTop: number
    winW: number
    winH: number
    // Current hour raster data
    currentU: Float32Array
    currentV: Float32Array
    currentTs: string
    // Next hour raster data (pre-loaded for interpolation)
    nextU: Float32Array | null
    nextV: Float32Array | null
    nextTs: string
  } | null = null

  constructor(config: NwmVariableConfig) {
    this.config = config
    this.protocol = `nwm-${config.id}`
    this.buffers = [
      { sourceId: `nwm-${config.id}-source-a`, layerId: `nwm-${config.id}-layer-a` },
      { sourceId: `nwm-${config.id}-source-b`, layerId: `nwm-${config.id}-layer-b` },
    ] as const
    this.windSourceId = `nwm-${config.id}-geojson`
    this.windLayerId = `nwm-${config.id}-lines`
  }

  get enabled(): boolean {
    return this._enabled
  }
  get opacity(): number {
    return this._opacity
  }
  get smoothing(): boolean {
    return this._smoothing
  }
  get enabledPrecipTypes(): number {
    return this._enabledPrecipTypes
  }
  set enabledPrecipTypes(mask: number) {
    if (this._enabledPrecipTypes === mask) return
    this._enabledPrecipTypes = mask
    this.refreshTiles()
  }
  get selectedTimestamp(): string {
    return this._selectedTimestamp
  }
  get availableTimestamps(): string[] {
    return this._availableTimestamps
  }
  get id(): NwmVariable {
    return this.config.id
  }
  get label(): string {
    return this.config.label
  }
  get description(): string {
    return this.config.description
  }
  get variableConfig(): NwmVariableConfig {
    return this.config
  }

  // ---- Timeline listener ----

  private onTimelineChange = (state: Readonly<TimelineState>): void => {
    if (this.suppressTimelineUpdates) return
    const ts = snapToAvailableTimestamp(state.currentTime, this._availableTimestamps)

    if (this.config.isVector && this._enabled) {
      // Wind: support smooth interpolation between hours
      const hourChanged =
        ts && ts !== this._selectedTimestamp && this._availableTimestamps.includes(ts)
      if (hourChanged) {
        this._selectedTimestamp = ts
        // Shift next→current if the next hour's data matches the new hour
        if (this.windCache?.nextTs === ts && this.windCache.nextU) {
          this.windCache.currentU = this.windCache.nextU
          this.windCache.currentV = this.windCache.nextV!
          this.windCache.currentTs = ts
          this.windCache.nextU = null
          this.windCache.nextV = null
          this.windCache.nextTs = ''
          void this.preloadWindNextHour()
        } else if (this.windLoading) {
          this.windOnDeckTs = ts
        } else {
          this.windOnDeckTs = ''
          void this.updateWindFeatures()
          return
        }
      }
      // Compute interpolation fraction and regenerate
      if (this.windCache) {
        const fraction = computeWindFraction(
          state.currentTime,
          this.windCache.currentTs,
          this._availableTimestamps,
          !!this.windCache.nextU
        )
        this.updateWindSource(fraction)
      }
      return
    }

    if (ts && ts !== this._selectedTimestamp && this._availableTimestamps.includes(ts)) {
      this._selectedTimestamp = ts
      if (this.backBufferLoading) {
        this.onDeckTimestamp = ts
      } else {
        this.onDeckTimestamp = ''
        this.refreshTiles()
      }
    }
  }

  // timestampFromTime extracted to nwm-utils.ts as snapToAvailableTimestamp

  // ---- Worker pool ----

  private initWorkers(): void {
    if (this.workers.length > 0) {
      return
    }
    for (let i = 0; i < WORKER_POOL_SIZE; i++) {
      this.workers.push(new NwmWorker())
    }
  }

  private getWorker(): Worker {
    const worker = this.workers[this.workerIndex]
    this.workerIndex = (this.workerIndex + 1) % this.workers.length
    return worker
  }

  private terminateWorkers(): void {
    this.workers.forEach(w => w.terminate())
    this.workers = []
    this.workerIndex = 0
  }

  // ---- Lifecycle ----

  register(map: maplibregl.Map): void {
    this.map = map
    if (!this.config.isVector) {
      addProtocol(this.protocol, (params, abortController) => {
        return this.handleTileRequest(params, abortController).catch(e => {
          if (e instanceof DOMException && e.name === 'AbortError') throw e
          log.warn(`NWM ${this.config.id} protocol error:`, e instanceof Error ? e.message : e)
          return { data: this.transparentTile() }
        })
      })
    }
    this.discoverTimestamps()
    timelineService.addListener(this.onTimelineChange)
  }

  unregister(): void {
    this.disable()
    timelineService.removeListener(this.onTimelineChange)
    if (!this.config.isVector) {
      try {
        removeProtocol(this.protocol)
      } catch (_) {
        /* may not be registered */
      }
    }
    this.terminateWorkers()
    this.cogCache.clear()
    this.map = null
  }

  enable(): void {
    if (!this.map || this._enabled) {
      return
    }

    if (this.config.isVector) {
      this.enableWindLayer()
      return
    }

    this.initWorkers()
    this._enabled = true
    this.frontBuffer = 0
    this.backBufferLoading = false

    this.addBuffer(0, this._opacity)
  }

  disable(): void {
    if (!this.map) {
      return
    }

    if (this.config.isVector) {
      this.disableWindLayer()
      return
    }

    this._enabled = false

    if (this.swapTimer) {
      clearTimeout(this.swapTimer)
      this.swapTimer = null
    }
    if (this.sourceDataHandler) {
      this.map.off('sourcedata', this.sourceDataHandler)
      this.sourceDataHandler = null
    }

    for (const [, controller] of this.pendingRequests) {
      controller.abort()
    }
    this.pendingRequests.clear()

    this.removeBuffer(0)
    this.removeBuffer(1)

    this.terminateWorkers()
  }

  toggle(): void {
    if (this._enabled) {
      this.disable()
    } else {
      this.enable()
    }
  }

  reAdd(): void {
    if (!this._enabled || !this.map) {
      return
    }

    if (this.config.isVector) {
      this.reAddWindLayer()
      return
    }

    this.initWorkers()
    const front = this.buffers[this.frontBuffer]
    if (!this.map.getSource(front.sourceId)) {
      this.addBuffer(this.frontBuffer, this._opacity)
    }
  }

  setOpacity(opacity: number): void {
    this._opacity = Math.max(0, Math.min(1, opacity))

    if (this.config.isVector) {
      if (this.map?.getLayer(this.windLayerId)) {
        this.map.setPaintProperty(this.windLayerId, 'line-opacity', this._opacity)
      }
      return
    }

    const front = this.buffers[this.frontBuffer]
    if (this.map?.getLayer(front.layerId)) {
      this.map.setPaintProperty(front.layerId, 'raster-opacity', this._opacity)
    }
  }

  setSmoothing(enabled: boolean): void {
    if (this._smoothing === enabled) return
    this._smoothing = enabled
    if (this.config.isVector) return
    const front = this.buffers[this.frontBuffer]
    if (this.map?.getLayer(front.layerId)) {
      this.map.setPaintProperty(front.layerId, 'raster-resampling', enabled ? 'linear' : 'nearest')
    }
  }

  // ---- Double-buffer management ----

  private addBuffer(index: number, opacity: number): void {
    if (!this.map) {
      return
    }
    const buf = this.buffers[index]

    if (!this.map.getSource(buf.sourceId)) {
      this.map.addSource(buf.sourceId, {
        type: 'raster',
        tiles: [`${this.protocol}://{z}/{x}/{y}?d=${this.dateVersion}`],
        tileSize: TILE_SIZE,
        minzoom: MIN_ZOOM,
        maxzoom: MAX_ZOOM,
        bounds: [-130, 22, -60, 55],
        attribution: `${this.config.label}: NOAA NWM`,
      })
    }

    if (!this.map.getLayer(buf.layerId)) {
      addOverlayLayer(
        this.map,
        {
          id: buf.layerId,
          type: 'raster',
          source: buf.sourceId,
          paint: {
            'raster-opacity': opacity,
            'raster-opacity-transition': { duration: 0, delay: 0 },
            'raster-fade-duration': 0,
            'raster-resampling': this._smoothing ? 'linear' : 'nearest',
          },
        },
        this.protocol
      )
    }
  }

  private removeBuffer(index: number): void {
    if (!this.map) {
      return
    }
    const buf = this.buffers[index]
    if (this.map.getLayer(buf.layerId)) {
      this.map.removeLayer(buf.layerId)
    }
    if (this.map.getSource(buf.sourceId)) {
      this.map.removeSource(buf.sourceId)
    }
  }

  private refreshTiles(): void {
    if (!this.map || !this._enabled) {
      return
    }

    if (this.config.isVector) {
      void this.updateWindFeatures()
      return
    }

    this.dateVersion++

    const backIndex = 1 - this.frontBuffer
    this.backBufferTimestamp = this._selectedTimestamp

    if (this.sourceDataHandler) {
      this.map.off('sourcedata', this.sourceDataHandler)
      this.sourceDataHandler = null
    }
    if (this.swapTimer) {
      clearTimeout(this.swapTimer)
      this.swapTimer = null
    }

    const primaryFile = this.getCogFilename()
    if (this.missingCogs.has(primaryFile)) {
      return
    }

    this.removeBuffer(backIndex)
    this.addBuffer(backIndex, 0)
    this.backBufferLoading = true

    const backSourceId = this.buffers[backIndex].sourceId
    const expectedTs = this._selectedTimestamp

    this.swapTimer = setTimeout(() => {
      if (this.backBufferLoading) {
        log.warn(`NWM ${this.config.id}: back buffer load timed out, forcing swap`)
        this.performSwap()
      }
    }, 15_000)
    this.sourceDataHandler = (e: maplibregl.MapSourceDataEvent) => {
      if (e.sourceId === backSourceId && e.isSourceLoaded) {
        if (this.swapTimer) {
          clearTimeout(this.swapTimer)
          this.swapTimer = null
        }
        this.map?.off('sourcedata', this.sourceDataHandler!)
        this.sourceDataHandler = null
        this.performSwap()
      }
    }
    this.map.on('sourcedata', this.sourceDataHandler)

    void this.probeCog(primaryFile, expectedTs)
  }

  private async probeCog(filename: string, expectedTimestamp: string): Promise<void> {
    try {
      const entry = await this.getCogEntry(filename)
      if (!entry) {
        if (this.backBufferLoading && this.backBufferTimestamp === expectedTimestamp) {
          this.cancelBackBufferLoad()
        }
        return
      }
      await this.getMetadata(entry)
    } catch {
      if (this.cogCache.has(filename)) {
        this.cogCache.delete(filename)
        this.missingCogs.add(filename)
      }
      if (this.backBufferLoading && this.backBufferTimestamp === expectedTimestamp) {
        this.cancelBackBufferLoad()
      }
    }
  }

  private cancelBackBufferLoad(): void {
    if (!this.backBufferLoading) return
    this.backBufferLoading = false
    if (this.swapTimer) {
      clearTimeout(this.swapTimer)
      this.swapTimer = null
    }
    if (this.sourceDataHandler && this.map) {
      this.map.off('sourcedata', this.sourceDataHandler)
      this.sourceDataHandler = null
    }
    const backIndex = 1 - this.frontBuffer
    this.removeBuffer(backIndex)

    if (this.onDeckTimestamp) {
      const next = this.onDeckTimestamp
      this.onDeckTimestamp = ''
      this._selectedTimestamp = next
      this.refreshTiles()
    }
  }

  private performSwap(): void {
    if (!this.backBufferLoading || !this.map || !this._enabled) {
      return
    }

    // If the COG was discovered missing while tiles were loading (race
    // between probeCog and sourcedata), cancel instead of swapping in
    // an all-transparent back buffer that would hide the valid front.
    const v = this.config.ncVariables[0].toLowerCase()
    const cogFile = `${this.cogPrefix}_${v}_${this.backBufferTimestamp}`
    if (this.missingCogs.has(cogFile)) {
      this.cancelBackBufferLoad()
      return
    }

    this.backBufferLoading = false
    if (this.swapTimer) {
      clearTimeout(this.swapTimer)
      this.swapTimer = null
    }

    const ts = this.backBufferTimestamp
    const oldFront = this.frontBuffer
    const backIndex = 1 - oldFront
    const back = this.buffers[backIndex]

    if (this.map.getLayer(back.layerId)) {
      this.map.setPaintProperty(back.layerId, 'raster-opacity', this._opacity)
    }

    const oldFrontLayer = this.buffers[oldFront].layerId
    if (this.map.getLayer(oldFrontLayer)) {
      this.map.setPaintProperty(oldFrontLayer, 'raster-opacity', 0)
    }

    this.frontBuffer = backIndex

    const dv = this.dateVersion
    this.map.once('render', () => {
      if (this.dateVersion !== dv) return
      this.removeBuffer(oldFront)
    })

    if (this.onDeckTimestamp && this.onDeckTimestamp !== ts) {
      this._selectedTimestamp = this.onDeckTimestamp
      this.onDeckTimestamp = ''
      this.refreshTiles()
    }
  }

  // ---- Timestamp discovery ----

  private discoverTimestamps(): void {
    this.missingCogs.clear()
    const candidates =
      this.config.source === 'goes' || this.config.source === 'mrms'
        ? generateGoesTimestampCandidates(new Date())
        : generateTimestampCandidates(new Date())
    this._availableTimestamps = candidates.sort()
    if (this._availableTimestamps.length > 0 && !this._selectedTimestamp) {
      this._selectedTimestamp = snapToAvailableTimestamp(
        timelineService.getState().currentTime,
        this._availableTimestamps
      )
    }
    if (this._enabled) {
      if (this._selectedTimestamp) {
        this.refreshTiles()
      }
    }
  }

  // generateTimestampCandidates extracted to nwm-utils.ts

  // ---- COG management ----

  private get cogBaseUrl(): string {
    if (this.config.source === 'goes') return `${COG_ORIGIN}/data/goes`
    if (this.config.source === 'mrms') return `${COG_ORIGIN}/data/mrms`
    return COG_BASE_URL
  }

  private get cogPrefix(): string {
    if (this.config.source === 'goes') return 'goes'
    if (this.config.source === 'mrms') return 'mrms'
    return 'nwm'
  }

  private get secondaryBaseUrl(): string {
    const src = this.config.secondaryCogSource ?? this.config.source
    if (src === 'goes') return `${COG_ORIGIN}/data/goes`
    if (src === 'mrms') return `${COG_ORIGIN}/data/mrms`
    return COG_BASE_URL
  }

  private get secondaryPrefix(): string {
    const src = this.config.secondaryCogSource ?? this.config.source
    if (src === 'goes') return 'goes'
    if (src === 'mrms') return 'mrms'
    return 'nwm'
  }

  private getCogFilename(varName?: string): string {
    const v = varName ?? this.config.ncVariables[0].toLowerCase()
    return `${this.cogPrefix}_${v}_${this._selectedTimestamp}`
  }

  private evictBrokenCog(): void {
    const filename = this.getCogFilename()
    if (this.cogCache.has(filename)) {
      this.cogCache.delete(filename)
      this.missingCogs.add(filename)
    }
  }

  private missingCogs = new Set<string>()

  private async getCogEntry(filename: string, baseUrl?: string): Promise<CogCacheEntry | null> {
    if (this.missingCogs.has(filename)) {
      return null
    }

    const cached = this.cogCache.get(filename)
    if (cached) {
      return cached
    }

    const pending = this.cogPending.get(filename)
    if (pending) {
      return pending
    }

    const promise = this.openCog(filename, baseUrl)
    this.cogPending.set(filename, promise)
    try {
      return await promise
    } finally {
      this.cogPending.delete(filename)
    }
  }

  private async openCog(filename: string, baseUrl?: string): Promise<CogCacheEntry | null> {
    const maxCache = COG_CACHE_SIZES[this.config.source] ?? 8
    if (this.cogCache.size >= maxCache) {
      const firstKey = this.cogCache.keys().next().value!
      this.cogCache.delete(firstKey)
    }

    const url = `${baseUrl ?? this.cogBaseUrl}/${filename}.tif`
    try {
      const tiff = await fromUrl(url, { blockSize: 65536, cacheSize: 100 } as Parameters<
        typeof fromUrl
      >[1])
      const entry: CogCacheEntry = {
        tiff,
        images: new Map(),
        imagesPending: new Map(),
        metadataPending: undefined,
      }
      this.cogCache.set(filename, entry)
      return entry
    } catch {
      this.missingCogs.add(filename)
      return null
    }
  }

  private async getImage(entry: CogCacheEntry, overviewIndex: number): Promise<GeoTIFFImage> {
    const cached = entry.images.get(overviewIndex)
    if (cached) {
      return cached
    }

    const pending = entry.imagesPending.get(overviewIndex)
    if (pending) {
      return pending
    }

    const promise = entry.tiff.getImage(overviewIndex).then(image => {
      entry.images.set(overviewIndex, image)
      return image
    })
    entry.imagesPending.set(overviewIndex, promise)
    try {
      return await promise
    } finally {
      entry.imagesPending.delete(overviewIndex)
    }
  }

  // zoomToOverviewIndex extracted to nwm-utils.ts

  // tileBoundsToLatLng extracted to nwm-utils.ts

  // ---- Tile request handler ----

  private async handleTileRequest(
    params: { url: string },
    abortController: AbortController
  ): Promise<{ data: ArrayBuffer | ImageBitmap }> {
    const match = params.url.match(/(\d+)\/(\d+)\/(\d+)/)
    if (!match) {
      throw new Error(`Invalid NWM tile URL: ${params.url}`)
    }

    const z = parseInt(match[1])
    const x = parseInt(match[2])
    const y = parseInt(match[3])
    const key = `${z}/${x}/${y}`

    if (z < MIN_ZOOM || !this._selectedTimestamp) {
      return { data: this.transparentTile() }
    }

    this.pendingRequests.set(key, abortController)

    try {
      const bounds = tileBoundsToLatLng(z, x, y)

      // NWM/HRRR covers CONUS — skip tiles clearly outside
      if (bounds.east < -130 || bounds.west > -60 || bounds.north < 20 || bounds.south > 55) {
        return { data: this.transparentTile() }
      }

      const maxOverview =
        this.config.source === 'goes'
          ? this.config.id === 'goes_glm'
            ? 1
            : 2
          : this.config.source === 'mrms'
            ? 2
            : this.config.source === 'hrrr'
              ? 2
              : 3
      const overviewIndex = zoomToOverviewIndex(z, maxOverview)

      if (isAborted(abortController.signal)) {
        throw new DOMException('Aborted', 'AbortError')
      }

      // Load COG(s) and read rasters
      let workerMsg: Record<string, unknown>

      if (this.config.isVector) {
        // Wind: load U2D and V2D COGs
        const uFile = this.getCogFilename('u2d')
        const vFile = this.getCogFilename('v2d')
        const [uEntry, vEntry] = await Promise.all([
          this.getCogEntry(uFile),
          this.getCogEntry(vFile),
        ])

        if (!uEntry || !vEntry) {
          return { data: this.transparentTile() }
        }

        if (isAborted(abortController.signal)) {
          throw new DOMException('Aborted', 'AbortError')
        }

        const [uPixels, vPixels] = await Promise.all([
          this.readTilePixels(uEntry, overviewIndex, bounds, abortController),
          this.readTilePixels(vEntry, overviewIndex, bounds, abortController),
        ])

        workerMsg = {
          variable: this.config.id,
          pixelsU: uPixels,
          pixelsV: vPixels,
        }
      } else if (this.config.secondaryCogVariable) {
        // Combined layer: load primary + optional secondary COG
        const primaryFilename = this.getCogFilename()
        const secPrefix = this.config.secondaryCogSource ? this.secondaryPrefix : this.cogPrefix
        const secBaseUrl = this.config.secondaryCogSource ? this.secondaryBaseUrl : undefined
        // GOES/MRMS timestamps are YYYYMMDDHHMM; HRRR/NWM are YYYYMMDDHH.
        // Truncate to hourly when the secondary comes from a different source.
        const secTimestamp = this.config.secondaryCogSource
          ? this._selectedTimestamp.slice(0, 10)
          : this._selectedTimestamp
        const secondaryFilename = `${secPrefix}_${this.config.secondaryCogVariable}_${secTimestamp}`

        const isCloudComposite = this.config.id === 'tcdc' || this.config.id === 'goes_cloud'

        if (isCloudComposite) {
          const [primaryEntry, secondaryEntry] = await Promise.all([
            this.getCogEntry(primaryFilename),
            this.getCogEntry(secondaryFilename, secBaseUrl),
          ])

          if (!primaryEntry) {
            return { data: this.transparentTile() }
          }

          if (isAborted(abortController.signal)) {
            throw new DOMException('Aborted', 'AbortError')
          }

          const readPromises: [Promise<Float32Array>, Promise<Float32Array> | null] = [
            this.readTilePixels(primaryEntry, overviewIndex, bounds, abortController),
            secondaryEntry
              ? this.readTilePixels(secondaryEntry, overviewIndex, bounds, abortController)
              : null,
          ]

          const [primaryPixels, secondaryPixels] = await Promise.all([
            readPromises[0],
            readPromises[1] ?? Promise.resolve(null),
          ])

          workerMsg = {
            variable: this.config.id,
            pixels: primaryPixels,
            pixelsSecondary: secondaryPixels,
          }
        } else {
          const primaryEntry = await this.getCogEntry(primaryFilename)

          if (!primaryEntry) {
            return { data: this.transparentTile() }
          }

          if (isAborted(abortController.signal)) {
            throw new DOMException('Aborted', 'AbortError')
          }

          const primaryPixels = await this.readTilePixels(
            primaryEntry,
            overviewIndex,
            bounds,
            abortController
          )
          let typeData: {
            pixels: Float32Array
            srcW: number
            srcH: number
            tileLeft: number
            tileTop: number
            tileRight: number
            tileBottom: number
          } | null = null
          const secondaryEntry = await this.getCogEntry(secondaryFilename, secBaseUrl)
          if (secondaryEntry) {
            typeData = await this.readTypePixelsNative(
              secondaryEntry,
              overviewIndex,
              bounds,
              abortController
            )
            if (typeData.srcW === 0) typeData = null
          }

          workerMsg = {
            variable: this.config.id,
            pixels: primaryPixels,
            pixelsSecondary: typeData?.pixels ?? null,
            typeSrcW: typeData?.srcW ?? 0,
            typeSrcH: typeData?.srcH ?? 0,
            typeTileLeft: typeData?.tileLeft ?? 0,
            typeTileTop: typeData?.tileTop ?? 0,
            typeTileRight: typeData?.tileRight ?? 0,
            typeTileBottom: typeData?.tileBottom ?? 0,
            enabledPrecipTypes: this._enabledPrecipTypes,
          }
        }
      } else {
        // Scalar: load single COG
        const cogFilename = this.getCogFilename()
        const entry = await this.getCogEntry(cogFilename)

        if (!entry) {
          return { data: this.transparentTile() }
        }

        if (isAborted(abortController.signal)) {
          throw new DOMException('Aborted', 'AbortError')
        }

        const scalarPixels = await this.readTilePixels(
          entry,
          overviewIndex,
          bounds,
          abortController
        )
        workerMsg = {
          variable: this.config.id,
          pixels: scalarPixels,
        }
      }

      if (isAborted(abortController.signal)) {
        throw new DOMException('Aborted', 'AbortError')
      }

      const bitmap = await this.processInWorker(workerMsg, abortController.signal)

      return { data: bitmap }
    } catch (e: unknown) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        if (!abortController.signal.aborted) {
          log.warn(
            `NWM ${this.config.id} spurious AbortError for ${key}, treating as fetch failure`
          )
          this.evictBrokenCog()
          return { data: this.transparentTile() }
        }
        throw e
      }
      log.warn(
        `NWM ${this.config.id} tile error for ${key}:`,
        e instanceof Error ? e.message : String(e)
      )
      this.evictBrokenCog()
      return { data: this.transparentTile() }
    } finally {
      this.pendingRequests.delete(key)
    }
  }

  private async getMetadata(entry: CogCacheEntry): Promise<CogMetadata> {
    if (entry.metadata) return entry.metadata
    if (entry.metadataPending) return entry.metadataPending

    const promise = this.loadMetadata(entry)
    entry.metadataPending = promise
    try {
      return await promise
    } finally {
      entry.metadataPending = undefined
    }
  }

  private async loadMetadata(entry: CogCacheEntry): Promise<CogMetadata> {
    const mainImage = await this.getImage(entry, 0)
    const bbox = mainImage.getBoundingBox()
    const mainWidth = mainImage.getWidth()
    const mainHeight = mainImage.getHeight()
    entry.metadata = {
      bbox,
      mainWidth,
      mainHeight,
      pxPerDegX: mainWidth / (bbox[2] - bbox[0]),
      pxPerDegY: mainHeight / (bbox[3] - bbox[1]),
    }
    return entry.metadata
  }

  private async readTilePixels(
    entry: CogCacheEntry,
    overviewIndex: number,
    bounds: { west: number; south: number; east: number; north: number },
    abortController: AbortController,
    resampleMethod: 'bilinear' | 'nearest' = 'bilinear'
  ): Promise<Float32Array> {
    const meta = await this.getMetadata(entry)
    const image = await this.getImage(entry, overviewIndex)

    if (isAborted(abortController.signal)) {
      throw new DOMException('Aborted', 'AbortError')
    }

    const { bbox, mainWidth, mainHeight, pxPerDegX, pxPerDegY } = meta
    const imgWidth = image.getWidth()
    const imgHeight = image.getHeight()
    const scaleX = imgWidth / mainWidth
    const scaleY = imgHeight / mainHeight

    const pixelLeft = Math.floor((bounds.west - bbox[0]) * pxPerDegX * scaleX)
    const pixelRight = Math.ceil((bounds.east - bbox[0]) * pxPerDegX * scaleX)
    const pixelTop = Math.floor((bbox[3] - bounds.north) * pxPerDegY * scaleY)
    const pixelBottom = Math.ceil((bbox[3] - bounds.south) * pxPerDegY * scaleY)

    const winLeft = Math.max(0, pixelLeft)
    const winTop = Math.max(0, pixelTop)
    const winRight = Math.min(imgWidth, pixelRight)
    const winBottom = Math.min(imgHeight, pixelBottom)

    const winWidth = winRight - winLeft
    const winHeight = winBottom - winTop

    if (winWidth <= 0 || winHeight <= 0) {
      return new Float32Array(TILE_SIZE * TILE_SIZE)
    }

    const rasters = await image.readRasters({
      window: [winLeft, winTop, winRight, winBottom],
      width: TILE_SIZE,
      height: TILE_SIZE,
      interleave: false,
      resampleMethod,
    })

    return new Float32Array(rasters[0] as unknown as ArrayBuffer)
  }

  private async readTypePixelsNative(
    entry: CogCacheEntry,
    overviewIndex: number,
    bounds: { west: number; south: number; east: number; north: number },
    abortController: AbortController
  ): Promise<{
    pixels: Float32Array
    srcW: number
    srcH: number
    tileLeft: number
    tileTop: number
    tileRight: number
    tileBottom: number
  }> {
    const meta = await this.getMetadata(entry)
    const image = await this.getImage(entry, overviewIndex)

    if (isAborted(abortController.signal)) {
      throw new DOMException('Aborted', 'AbortError')
    }

    const { bbox, mainWidth, mainHeight, pxPerDegX, pxPerDegY } = meta
    const imgWidth = image.getWidth()
    const imgHeight = image.getHeight()
    const scaleX = imgWidth / mainWidth
    const scaleY = imgHeight / mainHeight

    // 2 source cells of padding: 1 for the 3x3 kernel + 1 for bilinear at tile edges
    const pixelLeft = Math.floor((bounds.west - bbox[0]) * pxPerDegX * scaleX) - 2
    const pixelRight = Math.ceil((bounds.east - bbox[0]) * pxPerDegX * scaleX) + 2
    const pixelTop = Math.floor((bbox[3] - bounds.north) * pxPerDegY * scaleY) - 2
    const pixelBottom = Math.ceil((bbox[3] - bounds.south) * pxPerDegY * scaleY) + 2

    const winLeft = Math.max(0, pixelLeft)
    const winTop = Math.max(0, pixelTop)
    const winRight = Math.min(imgWidth, pixelRight)
    const winBottom = Math.min(imgHeight, pixelBottom)

    const srcW = winRight - winLeft
    const srcH = winBottom - winTop

    if (srcW <= 0 || srcH <= 0) {
      return {
        pixels: new Float32Array(0),
        srcW: 0,
        srcH: 0,
        tileLeft: 0,
        tileTop: 0,
        tileRight: 0,
        tileBottom: 0,
      }
    }

    // Exact source coordinates of the tile bounds, relative to the read window
    const exactLeft = (bounds.west - bbox[0]) * pxPerDegX * scaleX
    const exactTop = (bbox[3] - bounds.north) * pxPerDegY * scaleY
    const exactRight = (bounds.east - bbox[0]) * pxPerDegX * scaleX
    const exactBottom = (bbox[3] - bounds.south) * pxPerDegY * scaleY

    const rasters = await image.readRasters({
      window: [winLeft, winTop, winRight, winBottom],
      width: srcW,
      height: srcH,
      interleave: false,
      resampleMethod: 'nearest',
    })

    return {
      pixels: new Float32Array(rasters[0] as unknown as ArrayBuffer),
      srcW,
      srcH,
      tileLeft: exactLeft - winLeft,
      tileTop: exactTop - winTop,
      tileRight: exactRight - winLeft,
      tileBottom: exactBottom - winTop,
    }
  }

  private processInWorker(msg: Record<string, unknown>, signal: AbortSignal): Promise<ImageBitmap> {
    return new Promise((resolve, reject) => {
      if (signal.aborted) {
        reject(new DOMException('Aborted', 'AbortError'))
        return
      }

      const requestId = this.nextRequestId++
      const worker = this.getWorker()
      let aborted = false

      const timeout = setTimeout(() => {
        cleanup()
        reject(new Error('NWM worker timeout'))
      }, 30_000)

      const cleanup = (): void => {
        clearTimeout(timeout)
        worker.removeEventListener('message', handler)
        worker.removeEventListener('error', onError)
        signal.removeEventListener('abort', onAbort)
      }

      const handler = (e: MessageEvent): void => {
        if (e.data.requestId !== requestId) return
        if (aborted) {
          const bitmap = e.data.bitmap as ImageBitmap
          if (bitmap && typeof bitmap.close === 'function') bitmap.close()
          cleanup()
          return
        }
        cleanup()
        if (e.data.error) {
          reject(new Error(e.data.error))
        } else {
          resolve(e.data.bitmap as ImageBitmap)
        }
      }
      const onAbort = (): void => {
        aborted = true
        cleanup()
        reject(new DOMException('Aborted', 'AbortError'))
      }
      const onError = (ev: ErrorEvent): void => {
        cleanup()
        reject(new Error(ev.message || 'NWM worker error'))
      }

      worker.addEventListener('message', handler)
      worker.addEventListener('error', onError)
      signal.addEventListener('abort', onAbort)

      // Build transferables list
      const transferables: Transferable[] = []
      if (msg.pixels) {
        transferables.push((msg.pixels as Float32Array).buffer as ArrayBuffer)
      }
      if (msg.pixelsU) {
        transferables.push((msg.pixelsU as Float32Array).buffer as ArrayBuffer)
      }
      if (msg.pixelsV) {
        transferables.push((msg.pixelsV as Float32Array).buffer as ArrayBuffer)
      }
      if (msg.pixelsSecondary) {
        transferables.push((msg.pixelsSecondary as Float32Array).buffer as ArrayBuffer)
      }

      worker.postMessage({ ...msg, requestId }, transferables)
    })
  }

  private transparentTile(): ArrayBuffer {
    const png = new Uint8Array([
      0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a, 0x00, 0x00, 0x00, 0x0d, 0x49, 0x48, 0x44,
      0x52, 0x00, 0x00, 0x00, 0x01, 0x00, 0x00, 0x00, 0x01, 0x08, 0x06, 0x00, 0x00, 0x00, 0x1f,
      0x15, 0xc4, 0x89, 0x00, 0x00, 0x00, 0x0a, 0x49, 0x44, 0x41, 0x54, 0x78, 0x9c, 0x62, 0x00,
      0x00, 0x00, 0x02, 0x00, 0x01, 0xe5, 0x27, 0xde, 0xfc, 0x00, 0x00, 0x00, 0x00, 0x49, 0x45,
      0x4e, 0x44, 0xae, 0x42, 0x60, 0x82,
    ])
    return png.buffer
  }

  // ---- Wind GeoJSON layer ----

  private addWindSourceAndLayer(): void {
    if (!this.map) {
      return
    }
    if (!this.map.getSource(this.windSourceId)) {
      this.map.addSource(this.windSourceId, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
    }
    if (!this.map.getLayer(this.windLayerId)) {
      addOverlayLayer(
        this.map,
        {
          id: this.windLayerId,
          type: 'line',
          source: this.windSourceId,
          paint: {
            'line-color': [
              'interpolate',
              ['linear'],
              ['get', 'speed'],
              0,
              '#c8e6c9',
              3,
              '#90ca95',
              8,
              '#ffeb3b',
              15,
              '#ff9800',
              25,
              '#f44336',
              40,
              '#9c27b0',
              60,
              '#673ab7',
            ],
            'line-width': ['interpolate', ['linear'], ['zoom'], 4, 1, 7, 1.5, 10, 2, 12, 2.5],
            'line-opacity': this._opacity,
          },
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
        },
        this.protocol
      )
    }
  }

  private attachWindListeners(): void {
    if (!this.map) {
      return
    }
    if (this.windMoveHandler) {
      this.map.off('moveend', this.windMoveHandler)
      this.map.off('zoomend', this.windMoveHandler)
    }
    this.windMoveHandler = () => {
      if (this.windDebounceTimer) {
        clearTimeout(this.windDebounceTimer)
      }
      this.windDebounceTimer = setTimeout((): void => {
        void this.updateWindFeatures()
      }, 150)
    }
    this.map.on('moveend', this.windMoveHandler)
    this.map.on('zoomend', this.windMoveHandler)
  }

  private suppressTimelineUpdates = false

  private enableWindLayer(): void {
    if (!this.map) {
      return
    }
    this._enabled = true
    this.addWindSourceAndLayer()
    this.suppressTimelineUpdates = true
    this.suppressTimelineUpdates = false
    this.attachWindListeners()
    void this.updateWindFeatures()
  }

  private disableWindLayer(): void {
    if (!this.map) {
      return
    }
    this._enabled = false

    if (this.windDebounceTimer) {
      clearTimeout(this.windDebounceTimer)
      this.windDebounceTimer = null
    }
    if (this.windMoveHandler) {
      this.map.off('moveend', this.windMoveHandler)
      this.map.off('zoomend', this.windMoveHandler)
      this.windMoveHandler = null
    }
    this.windOnDeckTs = ''
    this.windViewportDirty = false
    this.windCache = null

    if (this.map.getLayer(this.windLayerId)) {
      this.map.removeLayer(this.windLayerId)
    }
    if (this.map.getSource(this.windSourceId)) {
      this.map.removeSource(this.windSourceId)
    }
  }

  private reAddWindLayer(): void {
    if (!this.map || !this._enabled) {
      return
    }
    this.addWindSourceAndLayer()
    this.attachWindListeners()
    void this.updateWindFeatures()
  }

  /**
   * Full wind data load: reads COG rasters for the current viewport,
   * caches them, pre-loads next hour, and generates features.
   * Called on viewport change (moveend/zoomend) or hour change.
   */
  private async updateWindFeatures(): Promise<void> {
    if (!this.map || !this._enabled || !this._selectedTimestamp) {
      return
    }
    if (this.windLoading) {
      this.windOnDeckTs = this._selectedTimestamp
      this.windViewportDirty = true
      return
    }

    this.windLoading = true
    const bounds = this.map.getBounds()
    const zoom = this.map.getZoom()

    const lngSpan = bounds.getEast() - bounds.getWest()
    const latSpan = bounds.getNorth() - bounds.getSouth()
    const spacing = Math.max(lngSpan, latSpan) / 20

    const buffer = spacing * 2
    const west = Math.max(-130, bounds.getWest() - buffer)
    const east = Math.min(-60, bounds.getEast() + buffer)
    const south = Math.max(20, bounds.getSouth() - buffer)
    const north = Math.min(55, bounds.getNorth() + buffer)

    if (west >= east || south >= north) {
      this.windLoading = false
      return
    }

    const ts = this._selectedTimestamp
    try {
      const uFile = `nwm_u2d_${ts}`
      const vFile = `nwm_v2d_${ts}`

      const [uEntry, vEntry] = await Promise.all([this.getCogEntry(uFile), this.getCogEntry(vFile)])

      if (!uEntry || !vEntry) {
        this.windLoading = false
        return
      }

      const meta = await this.getMetadata(uEntry)
      const { bbox, mainWidth: mainW, mainHeight: mainH, pxPerDegX, pxPerDegY } = meta

      const overviewIndex = zoomToOverviewIndex(zoom)
      const [uOvr, vOvr] = await Promise.all([
        this.getImage(uEntry, overviewIndex),
        this.getImage(vEntry, overviewIndex),
      ])

      const ovrW = uOvr.getWidth()
      const ovrH = uOvr.getHeight()
      const scaleX = ovrW / mainW
      const scaleY = ovrH / mainH

      const winLeft = Math.max(0, Math.floor((west - bbox[0]) * pxPerDegX * scaleX))
      const winRight = Math.min(ovrW, Math.ceil((east - bbox[0]) * pxPerDegX * scaleX))
      const winTop = Math.max(0, Math.floor((bbox[3] - north) * pxPerDegY * scaleY))
      const winBottom = Math.min(ovrH, Math.ceil((bbox[3] - south) * pxPerDegY * scaleY))

      const winW = winRight - winLeft
      const winH = winBottom - winTop
      if (winW <= 0 || winH <= 0) {
        this.windLoading = false
        return
      }

      const [uRasters, vRasters] = await Promise.all([
        uOvr.readRasters({ window: [winLeft, winTop, winRight, winBottom] }),
        vOvr.readRasters({ window: [winLeft, winTop, winRight, winBottom] }),
      ])

      this.windCache = {
        spacing,
        west,
        east,
        south,
        north,
        bbox,
        pxPerDegX,
        pxPerDegY,
        scaleX,
        scaleY,
        winLeft,
        winTop,
        winW,
        winH,
        currentU: new Float32Array(uRasters[0] as unknown as ArrayBuffer),
        currentV: new Float32Array(vRasters[0] as unknown as ArrayBuffer),
        currentTs: ts,
        nextU: null,
        nextV: null,
        nextTs: '',
      }

      this.updateWindSource(0)
      void this.preloadWindNextHour()
    } catch (e: unknown) {
      log.warn('[wind] updateWindFeatures error:', e instanceof Error ? e.message : String(e))
    } finally {
      this.windLoading = false
      const pendingTs = this.windOnDeckTs
      const pendingViewport = this.windViewportDirty
      this.windOnDeckTs = ''
      this.windViewportDirty = false
      if (pendingTs && pendingTs !== ts) {
        this._selectedTimestamp = pendingTs
        void this.updateWindFeatures()
      } else if (pendingViewport) {
        void this.updateWindFeatures()
      }
    }
  }

  /**
   * Pre-load the next hour's U/V rasters so interpolation is seamless.
   */
  private async preloadWindNextHour(): Promise<void> {
    if (!this.windCache) {
      return
    }
    const cache = this.windCache
    const tsIndex = this._availableTimestamps.indexOf(cache.currentTs)
    if (tsIndex < 0 || tsIndex >= this._availableTimestamps.length - 1) {
      return
    }

    const nextTs = this._availableTimestamps[tsIndex + 1]
    if (cache.nextTs === nextTs && cache.nextU) {
      return
    } // already loaded

    try {
      const [uEntry, vEntry] = await Promise.all([
        this.getCogEntry(`nwm_u2d_${nextTs}`),
        this.getCogEntry(`nwm_v2d_${nextTs}`),
      ])

      if (!uEntry || !vEntry) {
        return
      }

      const meta = await this.getMetadata(uEntry)
      const mainW = meta.mainWidth
      // Determine overview index by matching scale
      let ovrIdx = 0
      for (let i = 0; i <= 3; i++) {
        const img = await this.getImage(uEntry, i)
        if (Math.abs(img.getWidth() / mainW - cache.scaleX) < 0.01) {
          ovrIdx = i
          break
        }
      }

      const uOvr = await this.getImage(uEntry, ovrIdx)
      const vOvr = await this.getImage(vEntry, ovrIdx)

      const [uRasters, vRasters] = await Promise.all([
        uOvr.readRasters({
          window: [
            cache.winLeft,
            cache.winTop,
            cache.winLeft + cache.winW,
            cache.winTop + cache.winH,
          ],
        }),
        vOvr.readRasters({
          window: [
            cache.winLeft,
            cache.winTop,
            cache.winLeft + cache.winW,
            cache.winTop + cache.winH,
          ],
        }),
      ])

      // Only store if cache hasn't been invalidated
      if (this.windCache === cache) {
        cache.nextU = new Float32Array(uRasters[0] as unknown as ArrayBuffer)
        cache.nextV = new Float32Array(vRasters[0] as unknown as ArrayBuffer)
        cache.nextTs = nextTs
      }
    } catch (e: unknown) {
      // Pre-load failure is non-fatal — we just won't interpolate
      log.warn('[wind] preload next hour failed:', e instanceof Error ? e.message : String(e))
    }
  }

  // computeWindFraction and parseTimestamp extracted to nwm-utils.ts

  /**
   * Regenerate wind arrow GeoJSON and push it to the map source.
   * Delegates pure geometry to generateWindGeoJSON in nwm-utils.ts.
   */
  private updateWindSource(fraction: number): void {
    const cache = this.windCache
    if (!cache || !this.map) {
      return
    }

    const fc = generateWindGeoJSON(
      cache,
      cache.currentU,
      cache.currentV,
      fraction,
      cache.nextU,
      cache.nextV
    )

    const source = this.map.getSource(this.windSourceId)
    if (source && 'setData' in source) {
      ;(source as { setData: (data: GeoJSON.GeoJSON) => void }).setData(fc)
    }
  }

  // ---- Point query API ----

  async queryValueAtPoint(lat: number, lng: number): Promise<number | null> {
    if (!this._selectedTimestamp) {
      return null
    }
    if (lng < -130 || lng > -60 || lat < 20 || lat > 55) {
      return null
    }

    try {
      const cogFilename = this.getCogFilename()
      const entry = await this.getCogEntry(cogFilename)
      if (!entry) {
        return null
      }
      const meta = await this.getMetadata(entry)
      const { bbox, mainWidth: imgWidth, mainHeight: imgHeight } = meta

      const px = Math.floor(((lng - bbox[0]) / (bbox[2] - bbox[0])) * imgWidth)
      const py = Math.floor(((bbox[3] - lat) / (bbox[3] - bbox[1])) * imgHeight)

      if (px < 0 || px >= imgWidth || py < 0 || py >= imgHeight) {
        return null
      }

      const image = await this.getImage(entry, 0)
      const rasters = await image.readRasters({
        window: [px, py, px + 1, py + 1],
        width: 1,
        height: 1,
        interleave: false,
      })

      const value = (rasters[0] as unknown as number[])[0]
      if (value === -9999 || isNaN(value)) {
        return null
      }

      // Return in display units
      return this.config.transform ? this.config.transform(value) : value
    } catch {
      return null
    }
  }
}

// ---- Singleton instances ----

export const nwmSnowDepthService = new NwmVariableService(NWM_CONFIGS.snowh)
export const nwmTemperatureService = new NwmVariableService(NWM_CONFIGS.t2d)
export const nwmWindService = new NwmVariableService(NWM_CONFIGS.wind)
export const nwmPrecipCombinedService = new NwmVariableService(NWM_CONFIGS.precip)
export const nwmSnowfallService = new NwmVariableService(NWM_CONFIGS.qsnow)
export const nwmHumidityService = new NwmVariableService(NWM_CONFIGS.q2d)
export const nwmSolarService = new NwmVariableService(NWM_CONFIGS.swdown)
export const nwmLightningService = new NwmVariableService(NWM_CONFIGS.ltng)
export const nwmCloudCoverService = new NwmVariableService(NWM_CONFIGS.tcdc)
export const nwmSmokeService = new NwmVariableService(NWM_CONFIGS.smoke)
export const nwmReflectivityService = new NwmVariableService(NWM_CONFIGS.refc)
export const nwmVisibilityService = new NwmVariableService(NWM_CONFIGS.vis)
export const nwmCapeService = new NwmVariableService(NWM_CONFIGS.cape)
export const goesGlmService = new NwmVariableService(NWM_CONFIGS.goes_glm)
export const goesCloudService = new NwmVariableService(NWM_CONFIGS.goes_cloud)
export const mrmsRadarService = new NwmVariableService(NWM_CONFIGS.mrms_radar)

// Unified lightning over CONUS (HRRR LTNG → unified 0..1 index). Driven directly by
// the lightning coordinator (lightning-unified-service), NOT auto-dispatched, so it
// is intentionally kept OUT of ALL_NWM_SERVICES.
export const nwmLightningUnifiedService = new NwmVariableService(NWM_CONFIGS.lightning_unified)
/**
 * NWM service instances that are USER-FACING (registered with the map + driven by
 * the layer switcher). Layers removed from the UI in the weather reorg — Temperature
 * (t2d), Wind (wind), Precipitation (precip), CAPE (cape), Visibility (vis), Cloud
 * Cover (tcdc), Lightning Forecast (ltng), Smoke (smoke — replaced by the OM
 * om-smoke layer) — are deliberately OMITTED so they can never be registered,
 * enabled, or rendered from stale saved state (orphan-layer fix). Their service
 * instances still exist (exported) but are inert. Kept layers: snow depth, snowfall
 * rate, radar (refc), humidity (q2d), solar (swdown), and the observed GOES/MRMS set.
 */
export const ALL_NWM_SERVICES = [
  nwmSnowDepthService,
  nwmSnowfallService,
  nwmHumidityService,
  nwmSolarService,
  nwmReflectivityService,
  goesGlmService,
  goesCloudService,
  mrmsRadarService,
] as const

export { NwmVariableService }
